import React, { Suspense } from 'react';
import { PropTypes, shape, string } from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { Button } from '@ynap/button';
import ViewportObserver from '@ynap/viewport-observer';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import { upsellNavigation, trackAddToBag, trackAddToBagError, trackGoToBag, trackViewMoreNavigation } from '../../analytics';
import { ImageCarousel } from '../../index';
import ShopMore from '../../ShopMore/ShopMore';
import ReducedProductDetails from './ReducedProductDetails';

const bem = new BEMHelper('ReducedPDP');

const Recommendations = React.lazy(() => import(/* webpackChunkName: "Recommendations" */ '../../Recommendations/RecommendationsWrapper'));

const ReducedPDP = (props) => {
    const {
        product,
        messages,
        tracking,
        mappedCategories,
        locale,
        plpUrlTemplate,
        generateListingUrl,
        resetPLPState,
        ignoreCategories,
        bypassWCSRecommendations,
        language,
        soldOut,
    } = props;

    const safeTrackEvent = (buildAnalyticsFunc, ...args) => {
        const { analytics, tracking } = props;
        tracking.trackEvent(safeBuildEventObj(buildAnalyticsFunc, analytics, ...args));
    };

    const trackedAddToBag = (sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart }) => {
        const {
            addToCart,
            product: { sizeLabel, size },
        } = props;

        return addToCart(sku, { setActionNotificationOnError, addToCartLoading, reservationId, includeShoppingCart })
            .then((response) => {
                if (response && response.type === 'ADD_TO_CART_SUCCESS') {
                    return safeTrackEvent(trackAddToBag, sku, sizeLabel, null, size.schemas);
                } else {
                    return safeTrackEvent(trackAddToBagError, response.type);
                }
            })
            .catch((e) => e); //Catch any errors thrown from state
    };

    const trackedAddToBagError = () => {
        safeTrackEvent(trackAddToBagError, 'NO_SIZE_SELECTED');
    };

    const trackedGoToBag = (locale) => {
        const { redirectToCheckout } = props;
        safeTrackEvent(trackGoToBag);
        return redirectToCheckout(locale);
    };

    const viewSimilarAndShopMore =
        (mappedCategories?.length && (
            <>
                <Button
                    onClick={() =>
                        (window.location = generateListingUrl(plpUrlTemplate, {
                            LOCALE: locale,
                            SEO_URLKEYWORD: mappedCategories?.[mappedCategories?.length - 1]?.path,
                        }))
                    }
                    type="primary"
                    className={bem('viewSimilarItems')}
                >
                    {messages?.recommendations?.viewSimilarItems?.()}
                </Button>
                <div className={bem('shopMoreContainer')}>
                    <h3 className={bem('header')}>{messages.shopMore.title()}</h3>
                    <ShopMore
                        className={bem('shopMore')}
                        product={{ designerSeo: product?.designerSeo, designerName: product?.designerName }}
                        mappedCategories={mappedCategories}
                        locale={locale}
                        plpUrlTemplate={plpUrlTemplate}
                        generateListingUrl={generateListingUrl}
                        linkOnClick={() => {
                            resetPLPState();
                            tracking?.trackViewMoreNavigation;
                        }}
                        ignoreCategories={ignoreCategories}
                    />
                </div>
            </>
        )) ||
        null;

    return (
        <div className={bem('wrapper')}>
            <div className={bem('productDetailsGrid')}>
                <ImageCarousel
                    dir={language === 'ar' ? 'rtl' : 'ltr'}
                    images={product.images}
                    videos={product.videos}
                    className={{
                        carouselClassName: bem('imageCarousel'),
                    }}
                    reducedMode={true}
                    showThumbnails={false}
                    padding="noPadding"
                    allow2ndLevelZoom={true}
                    soldOut={soldOut}
                />
                <ReducedProductDetails
                    {...props}
                    tracking={{
                        trackedAddToBag: trackedAddToBag,
                        trackedAddToBagError: trackedAddToBagError,
                        trackedGoToBag: trackedGoToBag,
                        safeTrackEvent: safeTrackEvent,
                    }}
                />
            </div>

            <ViewportObserver className={bem('recommendationsWrapper')} innerKey={product.colourPartNumber}>
                {(intersected) =>
                    intersected && (
                        <Suspense fallback={''}>
                            <Recommendations
                                messages={messages}
                                colourPartNumber={product.colourPartNumber}
                                tracking={{
                                    wiw: (product, position) => {
                                        /* istanbul ignore next */
                                        safeTrackEvent(upsellNavigation, product, position, 'upsell - wear it with', `wear it with - look number ${position}`);
                                    },
                                    ymal: (product, position) => {
                                        /* istanbul ignore next */
                                        safeTrackEvent(upsellNavigation, product, position, 'upsell - ymal', 'ymal - module');
                                    },
                                    trackEvent: tracking.trackEvent,
                                    trackViewMoreNavigation: safeTrackEvent(trackViewMoreNavigation),
                                }}
                                reducedMode={true}
                                isCarouselVisible={false}
                                useOutfitView={false}
                                ymalFirst={true}
                                viewSimilarAndShopMore={viewSimilarAndShopMore}
                                bypassWCSRecommendations={bypassWCSRecommendations}
                            />
                        </Suspense>
                    )
                }
            </ViewportObserver>
        </div>
    );
};

ReducedPDP.propTypes = {
    product: PropTypes.object.isRequired,
    messages: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
    tracking: PropTypes.object.isRequired,
    analytics: shape({
        category: shape({
            pageType: string.isRequired,
            subCategory1: string.isRequired,
        }),
    }),
    addToCart: PropTypes.func,
    redirectToCheckout: PropTypes.func,
    mappedCategories: PropTypes.array,
    locale: PropTypes.string,
    plpUrlTemplate: PropTypes.string,
    generateListingUrl: PropTypes.func,
    resetPLPState: PropTypes.func,
    ignoreCategories: PropTypes.array,
    bypassWCSRecommendations: PropTypes.bool,
    language: PropTypes.string.isRequired,
};

export default withAnalytics(ReducedPDP);
